import * as React from 'react'

import { useHistory } from 'react-router'

import { useAppRoutes } from '@webapp/hooks'
import { useConversation } from './ConversationProvider'
import { useCart, type UseCartResult } from '../hooks/useCart'
import { type ConversationCartComponent } from '../value-objects/ConversationTurnItem'
import {
  ConversationComponentTypes,
  ConversationItemKinds,
} from '../value-objects/enums'

export type CartContextValue = UseCartResult

export const CartContext = React.createContext<Nullable<CartContextValue>>(null)

const selectLatestCart = (
  items: ReturnType<typeof useConversation>['items']
): Nullable<ConversationCartComponent> => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index]
    if (item.kind !== ConversationItemKinds.ENVELOPE) continue
    const cart = item.components.find(
      (component): component is ConversationCartComponent =>
        component.type === ConversationComponentTypes.CART
    )
    if (cart) return cart
  }
  return null
}

export const CartProvider = ({
  onContinue,
  children,
}: React.PropsWithChildren<{ onContinue: () => void }>) => {
  const { items, status, sendSilentMessage } = useConversation()
  const { url } = useAppRoutes()
  const history = useHistory()

  const cart = React.useMemo(() => selectLatestCart(items), [items])

  const handleContinued = React.useCallback(() => {
    history.push(url('OrderRequestPage'))
    onContinue()
  }, [history, url, onContinue])

  const value = useCart({
    cart,
    status,
    sendSilentMessage,
    onContinued: handleContinued,
  })

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCartContext = (): CartContextValue => {
  const context = React.useContext(CartContext)
  if (!context) {
    throw new Error('useCartContext must be used within a CartProvider')
  }
  return context
}
